import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import "./Question.scss";
import IconButton from "../IconButton/IconButton";
import ButtonWithIcon from "../ButtonwithIcon/ButtonWithIcon";
import { QUESTION_TYPE_LIST, getQuestionTypeMeta } from "./questionTypes";
import {
  createSingleChoiceOptions,
  createMultiChoiceOptions,
  createRatingOptions,
  resizeRatingOptions,
  MAX_RATING_COUNT,
  DEFAULT_RATING_COUNT,
} from "./scoringUtils";

// ─────────────────────────────────────────────────────────────────────────
// Question — same data contract as before:
//   questionText, answerType, questionOrder, answerOptions[{answerText,
//   score, isNew, isChanged, isDeleted}], isNew, isChanged
// Only the presentation changed: a collapsed summary card that expands
// into a guided editor, instead of one long always-visible row.
// ─────────────────────────────────────────────────────────────────────────

// `restricted` mirrors Section's/FormBuilder's restriction flag: true once
// the parent test already has responses. In that state this question can
// still be edited in place (question text, option wording, scores) but
// nothing new can be created and nothing existing can be removed —
// including switching the answer type (which would silently regenerate a
// fresh set of options) or resizing the rating scale.
const Question = ({
  question,
  questionIndex,
  onUpdate,
  onDelete,
  onDuplicate,
  dragHandleProps,
  defaultExpanded = false,
  focusToken,
  restricted = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const meta = getQuestionTypeMeta(question.answerType);

  const updateField = (field, value) => {
    onUpdate({ ...question, [field]: value, isChanged: !question.isNew });
  };

  const createDefaultOptions = (answerType) => {
    if (answerType === "SINGLE_CHOICE") return createSingleChoiceOptions();
    if (answerType === "MULTI_CHOICE") return createMultiChoiceOptions();
    if (answerType === "RATING")
      return createRatingOptions(DEFAULT_RATING_COUNT);
    return [];
  };

  useEffect(() => {
    // Once the test has responses, don't fabricate/regenerate options for
    // an existing question even if it happens to load with an unexpected
    // shape — restricted questions must keep exactly what they had.
    if (restricted) return;
    const opts = question.answerOptions || [];
    const needsDefaults =
      opts.length === 0 ||
      (question.answerType === "SINGLE_CHOICE" && opts.length !== 2);

    if (needsDefaults) {
      onUpdate({
        ...question,
        answerOptions: createDefaultOptions(question.answerType),
        isChanged: !question.isNew,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.answerType, restricted]);

  useEffect(() => {
    if (focusToken) setExpanded(true);
  }, [focusToken]);

  const handleAnswerTypeChange = (newType) => {
    if (newType === question.answerType) return;
    if (restricted) {
      toast.error(
        "This test has responses — the answer type can't be changed.",
      );
      return;
    }
    onUpdate({
      ...question,
      answerType: newType,
      answerOptions: createDefaultOptions(newType),
      isChanged: !question.isNew,
    });
  };

  const addOption = () => {
    if (question.answerType !== "MULTI_CHOICE") return;
    if (restricted) {
      toast.error("This test has responses — new options can't be added.");
      return;
    }
    updateField("answerOptions", [
      ...(question.answerOptions || []),
      {
        id: null,
        answerText: "",
        score: 1,
        isNew: true,
        isChanged: false,
        isDeleted: false,
      },
    ]);
  };

  const updateOption = (index, updatedOption) => {
    const opts = [...(question.answerOptions || [])];
    opts[index] = { ...updatedOption, isChanged: !updatedOption.isNew };
    updateField("answerOptions", opts);
  };

  const deleteOption = (index) => {
    if (question.answerType !== "MULTI_CHOICE") return;
    if (restricted) {
      toast.error("This test has responses — options can't be removed.");
      return;
    }
    const opts = question.answerOptions || [];
    if (opts.filter((o) => !o.isDeleted).length <= 1) return;
    const opt = opts[index];
    if (opt.isNew)
      updateField(
        "answerOptions",
        opts.filter((_, i) => i !== index),
      );
    else {
      const u = [...opts];
      u[index] = { ...opt, isDeleted: true };
      updateField("answerOptions", u);
    }
  };

  // ── RATING: pick how many rating points, then a score per point. ──────
  const ratingCount =
    (question.answerOptions || []).filter((o) => !o.isDeleted).length ||
    DEFAULT_RATING_COUNT;

  const handleRatingCountChange = (raw) => {
    if (restricted) {
      toast.error(
        "This test has responses — the rating scale can't be resized.",
      );
      return;
    }
    const next = resizeRatingOptions(raw, question.answerOptions || []);
    updateField("answerOptions", next);
  };

  const updateRatingOption = (index, field, value) => {
    const opts = [...(question.answerOptions || [])];
    const prior = opts[index];
    if (!prior) return;
    opts[index] = {
      ...prior,
      [field]: field === "score" ? Number(value) : value,
      isChanged: !prior.isNew,
    };
    updateField("answerOptions", opts);
  };

  const visibleOpts = (question.answerOptions || []).filter(
    (o) => !o.isDeleted,
  );
  const isEmpty = !question.questionText?.trim();

  return (
    <div
      className={`qcard ${expanded ? "qcard--open" : ""} ${isEmpty ? "qcard--empty" : ""}`}
      style={{ "--qt-color": meta.color, "--qt-color-soft": meta.colorSoft }}
    >
      {/* ── collapsed header row (always visible) ─────────────────────── */}
      <div className="qcard__head" onClick={() => setExpanded((v) => !v)}>
        <span
          className="qcard__handle"
          onClick={(e) => e.stopPropagation()}
          {...dragHandleProps}
          title="Drag to reorder"
        >
          <Icon icon="mdi:drag-vertical" />
        </span>

        <span className="qcard__num">{questionIndex}</span>

        <span className="qcard__type-badge" title={meta.label}>
          <Icon icon={meta.icon} />
        </span>

        <span className="qcard__title">
          {question.questionText?.trim() || (
            <em>Untitled question — click to edit</em>
          )}
        </span>

        <span className="qcard__meta">
          <span className="qcard__type-pill">{meta.shortLabel}</span>
          {question.answerType === "MULTI_CHOICE" && (
            <span className="qcard__opt-count">
              {visibleOpts.length} option{visibleOpts.length !== 1 ? "s" : ""}
            </span>
          )}
          {question.isNew && (
            <span className="qcard__badge qcard__badge--new">New</span>
          )}
          {question.isChanged && !question.isNew && (
            <span
              className="qcard__badge qcard__badge--mod"
              title="Unsaved changes"
            >
              •
            </span>
          )}
        </span>

        <span className="qcard__actions" onClick={(e) => e.stopPropagation()}>
          {!restricted && (
            <IconButton
              className="qcard__icon-btn"
              icon="mdi:content-copy"
              variant="edit"
              onClick={() => onDuplicate && onDuplicate()}
              title="Duplicate question"
            />
          )}
          {!restricted && (
            <IconButton
              className="qcard__icon-btn"
              icon="material-symbols-light:delete-outline"
              variant="delete"
              onClick={onDelete}
              title="Delete question"
            />
          )}
          <IconButton
            className="qcard__icon-btn qcard__chevron"
            icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
            variant="edit"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Collapse" : "Expand"}
          />
        </span>
      </div>

      {/* ── expanded editor ─────────────────────────────────────────────── */}
      {expanded && (
        <div className="qcard__body">
          <div className="qcard__question-row">
            <div className="qcard__field qcard__field--question">
              <label>Question</label>
              <input
                type="text"
                autoFocus={isEmpty}
                placeholder="Type your question here…"
                value={question.questionText || ""}
                onChange={(e) => updateField("questionText", e.target.value)}
              />
            </div>
            <div className="qcard__field qcard__field--xs">
              <label title="You can also drag the handle to reorder">
                Order
              </label>
              <input
                type="number"
                min="1"
                value={question.questionOrder || 1}
                onChange={(e) =>
                  updateField("questionOrder", Number(e.target.value))
                }
              />
            </div>
          </div>

          <div className="qcard__field">
            <label>Answer type</label>
            <div
              className={`qcard__type-picker ${restricted ? "qcard__type-picker--locked" : ""}`}
            >
              {QUESTION_TYPE_LIST.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  disabled={restricted && question.answerType !== t.value}
                  className={`qcard__type-opt ${question.answerType === t.value ? "qcard__type-opt--active" : ""}`}
                  style={{
                    "--opt-color": t.color,
                    "--opt-color-soft": t.colorSoft,
                  }}
                  onClick={() => handleAnswerTypeChange(t.value)}
                >
                  <Icon icon={t.icon} />
                  <span>
                    <strong>{t.label}</strong>
                    <small>{t.description}</small>
                  </span>
                </button>
              ))}
            </div>
            {restricted && (
              <p className="qcard__restricted-note">
                <Icon icon="mdi:lock-outline" /> This test has responses, so the
                answer type can't be changed.
              </p>
            )}
          </div>

          {/* ── multi-choice options ────────────────────────────────────── */}
          {question.answerType === "MULTI_CHOICE" && (
            <div className="qcard__opts">
              <div className="qcard__opts-hd">
                <span>Answer options</span>
                {!restricted && (
                  <ButtonWithIcon
                    className="add small"
                    icon="mdi:plus-circle-outline"
                    onClick={addOption}
                    text="Add option"
                    type="button"
                  />
                )}
              </div>
              {restricted && (
                <p className="qcard__restricted-note">
                  <Icon icon="mdi:lock-outline" /> This test has responses, so
                  existing options can be edited but none can be added or
                  removed.
                </p>
              )}

              <div className="qcard__opt-cols">
                <span />
                <span>Answer text</span>
                <span>Score</span>
                <span />
              </div>

              {visibleOpts.map((opt) => {
                const ai = question.answerOptions.findIndex((o) => o === opt);
                return (
                  <div key={ai} className="qcard__opt-row">
                    <span className="qcard__opt-num">{ai + 1}</span>
                    <input
                      placeholder="e.g. Strongly agree"
                      value={opt.answerText || ""}
                      onChange={(e) =>
                        updateOption(ai, {
                          ...opt,
                          answerText: e.target.value,
                        })
                      }
                    />
                    <input
                      type="number"
                      placeholder="0"
                      value={opt.score ?? 0}
                      onChange={(e) =>
                        updateOption(ai, {
                          ...opt,
                          score: Number(e.target.value),
                        })
                      }
                    />
                    {!restricted && visibleOpts.length > 1 && (
                      <IconButton
                        className="qcard__opt-del"
                        icon="material-symbols-light:delete-outline"
                        variant="delete"
                        onClick={() => deleteOption(ai)}
                        title="Remove this option"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {question.answerType === "SINGLE_CHOICE" && (
            <div className="qcard__opts">
              <p className="qcard__auto-hint">
                <Icon icon="mdi:auto-fix" />
                Options are Yes / No — type in the value each answer should
                score.
              </p>
              <div className="qcard__compact-options">
                {visibleOpts.map((opt, ai) => (
                  <div key={ai} className="qcard__compact-option">
                    <label>
                      {ai === 0 ? "Yes" : ai === 1 ? "No" : `Option ${ai + 1}`}
                    </label>
                    <input
                      aria-label={`Answer ${ai + 1}`}
                      value={opt.answerText || ""}
                      onChange={(e) =>
                        updateOption(ai, { ...opt, answerText: e.target.value })
                      }
                    />
                    <input
                      type="number"
                      aria-label={`${opt.answerText || `Option ${ai + 1}`} score`}
                      placeholder="0"
                      value={opt.score ?? 0}
                      onChange={(e) =>
                        updateOption(ai, {
                          ...opt,
                          score: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {question.answerType === "RATING" && (
            <div className="qcard__opts">
              <div className="qcard__opts-hd">
                <span>Rating scale</span>
                <label className="qcard__rating-count">
                  Number of ratings
                  <input
                    type="number"
                    min={1}
                    max={MAX_RATING_COUNT}
                    value={ratingCount}
                    disabled={restricted}
                    onChange={(e) => handleRatingCountChange(e.target.value)}
                  />
                </label>
              </div>
              {restricted && (
                <p className="qcard__restricted-note">
                  <Icon icon="mdi:lock-outline" /> This test has responses, so
                  the number of rating points can't be changed — but labels and
                  scores can still be edited.
                </p>
              )}

              <div className="qcard__compact-options qcard__compact-options--rating">
                {visibleOpts.map((opt, ai) => (
                  <div key={ai} className="qcard__compact-option">
                    <label>Rating {ai + 1}</label>
                    <input
                      aria-label={`Rating ${ai + 1} label`}
                      value={opt.answerText || ""}
                      onChange={(e) =>
                        updateRatingOption(ai, "answerText", e.target.value)
                      }
                    />
                    <input
                      type="number"
                      aria-label={`Rating ${ai + 1} score`}
                      placeholder="0"
                      value={opt.score ?? 0}
                      onChange={(e) =>
                        updateRatingOption(ai, "score", e.target.value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="qcard__preview">
            <span className="qcard__preview-label">
              <Icon icon="mdi:eye-outline" /> Respondent preview
            </span>
            <div className="qcard__preview-body">
              <p>{question.questionText?.trim() || "Untitled question"}</p>
              <div className="qcard__preview-opts">
                {visibleOpts.slice(0, 6).map((o, i) => (
                  <span key={i} className="qcard__preview-opt">
                    {o.answerText || `Option ${i + 1}`}
                  </span>
                ))}
                {visibleOpts.length > 6 && (
                  <span className="qcard__preview-opt qcard__preview-opt--more">
                    +{visibleOpts.length - 6} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Question;
